import { CreateToBackoffice, FetchFromBackoffice } from '../../../utils/requests';
import { VehicleBrand, VehicleModel, VehicleVariant } from './types';

/** The create response is not guaranteed to carry every field the list endpoint returns. */
type CreatedBrandResponse = Partial<VehicleBrand> & {
  id?: number;
  wmi?: string;
};

export const validateBrandInput = (name: string, wmi: string): string | null => {
  if (!name.trim()) {
    return 'Brand name is required.';
  }

  if (!/^[A-Z0-9]{3}$/.test(wmi.trim().toUpperCase())) {
    return 'WMI code must be exactly 3 uppercase letters or numbers.';
  }

  return null;
};

export const createVehicleBrand = async (name: string, wmi: string): Promise<VehicleBrand> => {
  const normalizedName = name.trim();
  const normalizedWmi = wmi.trim().toUpperCase();

  const created: CreatedBrandResponse | undefined = await CreateToBackoffice<CreatedBrandResponse>(
    'portal/vehicle/brands',
    { name: normalizedName, wmi: normalizedWmi }
  );

  const createdId = created?.brandId ?? created?.id;
  if (createdId && created?.name) {
    return {
      brandId: createdId,
      name: created.name,
      patterns: created.patterns?.length ? created.patterns : [created.wmi ?? normalizedWmi],
    };
  }

  // The create response was incomplete, so read the stored brand back from the list endpoint.
  const brands = await FetchFromBackoffice<VehicleBrand[]>('portal/vehicle/brands');
  const stored = (brands ?? []).find((item) => (
    (createdId !== undefined && item.brandId === createdId)
      || (item.name ?? '').trim().toLowerCase() === normalizedName.toLowerCase()
      || (item.patterns ?? []).some((pattern) => pattern.toUpperCase() === normalizedWmi)
  ));

  if (!stored) {
    throw new Error(`Brand ${normalizedName} was created but could not be read back.`);
  }

  return {
    ...stored,
    name: (stored.name ?? '').trim() || normalizedName,
    patterns: stored.patterns?.length ? stored.patterns : [normalizedWmi],
  };
};

/** The create response is not guaranteed to carry every field the list endpoint returns. */
type CreatedModelResponse = Partial<VehicleModel> & {
  id?: number;
};

export const createVehicleModel = async (brandId: number, name: string): Promise<VehicleModel> => {
  const normalizedName = name.trim();

  const created: CreatedModelResponse | undefined = await CreateToBackoffice<CreatedModelResponse>(
    `portal/vehicle/brands/${brandId}/models`,
    { name: normalizedName }
  );

  const createdId = created?.modelId ?? created?.id;
  if (createdId && created?.name) {
    return { modelId: createdId, name: created.name, brandId: created.brandId ?? brandId };
  }

  // The create response was incomplete, so read the stored model back from the list endpoint.
  const models = await FetchFromBackoffice<VehicleModel[]>(`portal/vehicle/brands/${brandId}/models`);
  const stored = (models ?? []).find((item) => (
    (createdId !== undefined && item.modelId === createdId)
      || (item.name ?? '').trim().toLowerCase() === normalizedName.toLowerCase()
  ));

  if (!stored) {
    throw new Error(`Model ${normalizedName} was created but could not be read back.`);
  }

  return {
    ...stored,
    name: (stored.name ?? '').trim() || normalizedName,
    brandId: stored.brandId ?? brandId,
  };
};

/** The create response is not guaranteed to carry every field the list endpoint returns. */
type CreatedVariantResponse = Partial<VehicleVariant> & {
  id?: number;
};

/** Exactly the fields the variant endpoints accept. */
type VehicleVariantPayload = {
  name: string;
  powertrain: string;
  fromYear: number;
  toYear: number | null;
  externalId: string;
  weight: number;
  length: number;
  height: number;
  width: number;
  regexPattern: string;
};

const toNullableYear = (year: number | string | null): number | null => (
  year === null || year === '' ? null : Number(year)
);

/**
 * A variant is either produced between two years or still in production, in which case the
 * end of production is unknown and `toYear` is null.
 */
export const validateVariantYears = (
  fromYear: number | string,
  toYear: number | string | null
): string | null => {
  const from = Number(fromYear);
  if (fromYear === '' || !Number.isInteger(from) || from < 1900) {
    return 'From year must be a valid year.';
  }

  const to = toNullableYear(toYear);
  if (to === null) {
    return null;
  }

  if (!Number.isInteger(to)) {
    return 'To year must be a valid year.';
  }

  if (to <= from) {
    return 'To year must be greater than from year, or empty for ongoing production.';
  }

  return null;
};

export const toVariantPayload = (variant: Omit<VehicleVariant, 'variantId'>): VehicleVariantPayload => ({
  name: variant.name.trim(),
  powertrain: variant.powertrain,
  fromYear: Number(variant.fromYear),
  toYear: toNullableYear(variant.toYear),
  externalId: variant.externalId ?? '',
  weight: variant.weight,
  length: variant.length,
  height: variant.height,
  width: variant.width,
  regexPattern: variant.regexPattern.trim(),
});

export const createVehicleVariant = async (
  brandId: number,
  modelId: number,
  variant: Omit<VehicleVariant, 'variantId'>
): Promise<VehicleVariant> => {
  const payload = toVariantPayload(variant);
  const yearError = validateVariantYears(payload.fromYear, payload.toYear);
  if (yearError) {
    throw new Error(yearError);
  }

  const created: CreatedVariantResponse | undefined = await CreateToBackoffice<CreatedVariantResponse>(
    `portal/vehicle/brands/${brandId}/models/${modelId}/variants`,
    payload
  );

  const createdId = created?.variantId ?? created?.id;
  if (createdId && created?.name) {
    return { ...variant, ...payload, ...created, variantId: createdId, name: created.name };
  }

  // The create response was incomplete, so read the stored variant back from the list endpoint.
  const variants = await FetchFromBackoffice<VehicleVariant[]>(
    `portal/vehicle/brands/${brandId}/models/${modelId}/variants`
  );
  const stored = (variants ?? []).find((item) => (
    (createdId !== undefined && item.variantId === createdId)
      || (item.name ?? '').trim().toLowerCase() === variant.name.trim().toLowerCase()
  ));

  if (!stored) {
    throw new Error(`Variant ${variant.name} was created but could not be read back.`);
  }

  return { ...variant, ...stored, name: (stored.name ?? '').trim() || variant.name };
};

const getFixedRegexLength = (pattern: string): number | null => {
  const source = pattern.replace(/^\^/, '').replace(/\$$/, '');
  let length = 0;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];

    if (character === '|') {
      return null;
    }

    if (character === '(' || character === ')') {
      continue;
    }

    if (character === '[') {
      const end = source.indexOf(']', index + 1);
      if (end < 0) {
        return null;
      }
      index = end;
    } else if (character === '\\') {
      index += 1;
      if (index >= source.length) {
        return null;
      }
    } else if (character === '{' || character === '}' || character === '?' || character === '*' || character === '+') {
      return null;
    }

    let multiplier = 1;
    if (source[index + 1] === '{') {
      const end = source.indexOf('}', index + 2);
      const quantifier = end >= 0 ? source.slice(index + 2, end) : '';
      if (!/^\d+$/.test(quantifier)) {
        return null;
      }
      multiplier = Number(quantifier);
      index = end;
    }

    length += multiplier;
  }

  return length;
};

export const validateVinRegex = (pattern: string, brandPatterns: string[]): string | null => {
  const trimmedPattern = pattern.trim();

  if (!trimmedPattern.startsWith('^')) {
    return 'VIN pattern must start with ^.';
  }

  if (!trimmedPattern.endsWith('$')) {
    return 'VIN pattern must end with $.';
  }

  const matchesBrandWmi = brandPatterns.some((brandPattern) =>
    trimmedPattern.startsWith(`^${brandPattern.trim()}`)
  );

  if (!matchesBrandWmi) {
    return `VIN pattern must start with ^ followed by one of: ${brandPatterns.join(', ')}`;
  }

  try {
    new RegExp(trimmedPattern);
  } catch {
    return 'VIN pattern is not a valid regular expression.';
  }

  if (getFixedRegexLength(trimmedPattern) !== 17) {
    return 'VIN pattern must match exactly 17 characters.';
  }

  return null;
};
